import React from "react";
import * as Mui from "@mui/material";
import {
  ApartmentRounded,
  ExpandLess,
  ExpandMore,
  GroupsRounded,
} from "@mui/icons-material";
import { designColors, designFontFamily } from "services/constants/designTokens";
import { getDepartmentScopeLabel, getDepartmentScopeValue } from "./DepartmentMultiSelect";

// Shared helpers for the User Group screens (list, view panel, add/edit panel).

// Long user lists show this many rows, then a "+N more users" row that
// expands them -- used by both the Add/Edit form and the View panel.
export const MEMBER_PREVIEW = 5;
const moreRowSx = {
  mt: 1,
  width: "100%",
  minHeight: 40,
  borderRadius: "8px",
  textTransform: "none",
  fontFamily: designFontFamily,
  fontSize: 13.5,
  fontWeight: 600,
  color: designColors.tealDark,
  backgroundColor: "#F0FAF7",
  "&:hover": { backgroundColor: "#E3F5EF" },
};
export const MoreRow = ({ expanded, hiddenCount, noun, onToggle }) => (
  <Mui.Button
    onClick={onToggle}
    startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
    sx={moreRowSx}
  >
    {expanded ? "Show less" : `+ ${hiddenCount} more ${noun}`}
  </Mui.Button>
);

// The API supports status or is_active; accept either representation.
export const getGroupStatus = (group) => {
  const raw = group?.status ?? group?.group_status ?? group?.is_active;
  if (typeof raw === "boolean") return raw ? "Active" : "Inactive";
  if (typeof raw === "string" && raw.trim()) {
    return raw.trim().toLowerCase() === "inactive" ? "Inactive" : "Active";
  }
  return "Active";
};

export const hasStatusField = (group) =>
  group?.status !== undefined || group?.group_status !== undefined || group?.is_active !== undefined;

// assignments[] holds one { company, role } pair per entry. A company that
// carries several roles comes back as several entries, so fold them into
// one row per company for display and editing.
export const groupAssignmentsByCompany = (assignments = []) => {
  const byCompany = new Map();
  (assignments ?? []).forEach((assignment) => {
    const key = assignment?.company_id ?? assignment?.company_name;
    if (key === undefined || key === null) return;
    if (!byCompany.has(key)) {
      byCompany.set(key, {
        company_id: assignment?.company_id,
        company_name: assignment?.company_name,
        roles: [],
      });
    }
    const entry = byCompany.get(key);
    if (
      (assignment?.role_id || assignment?.role_name) &&
      !entry.roles.some(
        (role) =>
          String(role.role_id) === String(assignment?.role_id) &&
          role.role_name === assignment?.role_name,
      )
    ) {
      entry.roles.push({ role_id: assignment?.role_id, role_name: assignment?.role_name });
    }
  });
  return [...byCompany.values()];
};

export const roleNames = (company) =>
  (company?.roles ?? []).map((role) => role?.role_name).filter(Boolean).join(" · ");

export const getInitials = (name = "") =>
  String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "?";

const hashString = (value = "") =>
  String(value)
    .split("")
    .reduce((hash, char) => (hash * 31 + char.charCodeAt(0)) >>> 0, 7);

const AVATAR_PALETTE = [
  { color: "#2563EB", bg: "#DBEAFE" },
  { color: "#7C3AED", bg: "#EDE9FE" },
  { color: "#0F766E", bg: "#CCFBF1" },
  { color: "#EA580C", bg: "#FFEDD5" },
  { color: "#DB2777", bg: "#FCE7F3" },
  { color: "#16A34A", bg: "#DCFCE7" },
];

const GROUP_PALETTE = [
  { color: "#DB2777", bg: "#FCE7F3" },
  { color: "#2563EB", bg: "#DBEAFE" },
  { color: "#16A34A", bg: "#DCFCE7" },
  { color: "#EA580C", bg: "#FFEDD5" },
  { color: "#DC2626", bg: "#FEE2E2" },
  { color: "#1D4ED8", bg: "#E0E7FF" },
  { color: "#0891B2", bg: "#CFFAFE" },
  { color: "#CA8A04", bg: "#FEF9C3" },
];

export const paletteFor = (seed, palette = AVATAR_PALETTE) =>
  palette[hashString(seed) % palette.length];

export const GroupIcon = ({ group, size = 40 }) => {
  const { color, bg } = paletteFor(group?.id ?? group?.name, GROUP_PALETTE);
  return (
    <Mui.Box
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "10px",
        backgroundColor: bg,
        color,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <GroupsRounded sx={{ fontSize: size * 0.55 }} />
    </Mui.Box>
  );
};

export const CompanyIcon = ({ size = 32, tinted = false }) => (
  <Mui.Box
    sx={{
      width: size,
      height: size,
      minWidth: size,
      borderRadius: tinted ? "50%" : "8px",
      backgroundColor: tinted ? "#E6F8F4" : "transparent",
      color: tinted ? "#0F766E" : designColors.slate700,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <ApartmentRounded sx={{ fontSize: size * (tinted ? 0.55 : 0.7) }} />
  </Mui.Box>
);

export const UserAvatar = ({ name, image, size = 34 }) => {
  const { color, bg } = paletteFor(name);
  return (
    <Mui.Avatar
      src={image || undefined}
      alt={name}
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        fontWeight: 700,
        fontFamily: designFontFamily,
        color,
        backgroundColor: bg,
      }}
    >
      {getInitials(name)}
    </Mui.Avatar>
  );
};

// Readable "Department Access" for a group member. The GET payload gives the
// scope as a label ("All Departments") plus the departments for the
// "Additional Departments" scope.
export const departmentAccessLabel = (user) => {
  const departments = (user?.departments ?? [])
    .map((dept) => dept?.department ?? dept?.name)
    .filter(Boolean);
  const scopeValue = getDepartmentScopeValue(user?.scope) ?? user?.scope;
  if (scopeValue === "additional" || (!user?.scope && departments.length)) {
    return departments.length ? departments.join(", ") : "Additional Departments";
  }
  // Known scopes show their label ("all" -> "All Departments").
  if (scopeValue) return getDepartmentScopeLabel(scopeValue);
  return departments.length ? departments.join(", ") : "-";
};

export const groupTypography = {
  cellStrong: {
    fontSize: 14,
    fontWeight: 700,
    fontFamily: designFontFamily,
    color: designColors.slate800,
  },
  cellMuted: {
    fontSize: 13,
    fontFamily: designFontFamily,
    color: designColors.slate600,
  },
};

// Distinct roles a group assigns, with how many companies use each one.
// Mirror of getGroupsForRole (roleUiKit): match on role_id, falling back to
// the role name for payloads that only carry the name. A role with neither
// is skipped.
export const getRolesForGroup = (assignments = []) => {
  const byRole = new Map();
  (assignments ?? []).forEach((assignment) => {
    const key =
      assignment?.role_id !== undefined && assignment?.role_id !== null
        ? `id:${assignment.role_id}`
        : assignment?.role_name
          ? `name:${assignment.role_name}`
          : null;
    if (!key) return;
    const entry = byRole.get(key) ?? {
      role_id: assignment?.role_id,
      role_name: assignment?.role_name,
      description: assignment?.role_description ?? assignment?.description,
      companyCount: 0,
    };
    // A nameless assignment still counts; pick the name up from any sibling.
    if (!entry.role_name && assignment?.role_name) entry.role_name = assignment.role_name;
    entry.companyCount += 1;
    byRole.set(key, entry);
  });
  return [...byRole.values()];
};
